import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { IconName } from '@sc/data-access-assets';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import { PhoneNumberPipe } from '@sc/feature-contextual-display';
import {
  Appointment,
  CLIENT_APP_ID,
  ClientAppId,
  Dossier,
  Fitter,
  RequestAppointment,
} from '@sc/data-access-models';
import { LocaleDatePipe } from '@sc/data-access-translation';
import {
  FITTER_NAME_DISPLAY_CONTEXT,
  FitterNameDisplayContext,
  FitterNameDisplayService,
  FitterNamePipe,
} from '@sc/feature-contextual-display';
import {
  ButtonComponent,
  IconComponent,
  MomentParseZonePipe,
} from '@sc/ui-components';
import {
  PlanningCalendarService,
  PlanningSandboxService,
} from '../../services';

@Component({
  selector: 'sc-planning-basket-appointment',
  styleUrls: ['./planning-basket-appointment.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    FitterNameDisplayService,
    {
      provide: FITTER_NAME_DISPLAY_CONTEXT,
      useValue: FitterNameDisplayContext.BasketItemHeader,
    },
  ],
  imports: [
    MatCardModule,
    NgIf,
    NgFor,
    IconComponent,
    ButtonComponent,
    AsyncPipe,
    PhoneNumberPipe,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
    FitterNamePipe,
  ],
  template: ` <mat-card appearance="outlined">
    <mat-card-title [class.no-time]="!dossier?.appointment?.time">
      <div *ngIf="!!dossier?.appointment?.time" class="title-time">
        {{
          dossier?.appointment?.time
            | parseZoneTimeString
              : timezoneService.getTimezone(dossier?.appointment)
            | localeDate: 'shortTime'
            | async
        }}
      </div>
      <div
        *ngIf="fitter"
        class="fitter-dossier uppercase"
        [style.background-color]="
          planningCalendarService.getDossierEventColor(dossier, fitter)
        "
      >
        {{ fitter | fitterName: 'basketItemHeader' | async }}
      </div>
    </mat-card-title>
    <mat-card-content
      [style.background]="
        planningCalendarService.getDossierEventBackground(dossier, fitter)
      "
      [style.border-color]="
        planningCalendarService.getDossierEventColor(dossier, fitter)
      "
    >
      <div>
        <p class="font-family-bold font-s">
          {{
            dossier?.claim?.car?.manufacturer && dossier?.claim?.car?.model
              ? dossier?.claim?.car?.manufacturer +
                ' ' +
                dossier?.claim?.car?.model
              : ('PlanningEventTooltip.vehicleNotFound' | translate)
          }}
        </p>
        <div>
          <sc-icon
            *ngFor="let interventionTypeIcon of interventionTypeIcons"
            [class]="'intervention-type ' + interventionTypeIcon"
            [name]="interventionTypeIcon"
          ></sc-icon>
          <sc-icon
            *ngIf="
              partAvailabilityIcon &&
              dossier?.claim?.operationType === 'Replace'
            "
            [class]="'replacement-part ' + partAvailabilityIcon"
            name="operation_vitre"
          ></sc-icon>

          <sc-icon
            *ngIf="dossier?.creationData?.origin !== 'AGENDA' && originIcon"
            class="origin"
            [name]="originIcon"
          ></sc-icon>
        </div>
      </div>
      <p
        class="font-family-main font-xs"
        *ngIf="dossier?.claim?.car?.licensePlateNumber"
      >
        {{ dossier?.claim?.car?.licensePlateNumber }}
      </p>
      <p
        class="font-family-main font-xs uppercase"
        *ngIf="dossier?.customer?.lastname"
      >
        {{ dossier?.customer?.lastname + ' ' + dossier?.customer?.firstname }}
      </p>
      <p
        class="font-family-main font-xs"
        *ngIf="dossier?.customer?.phoneNumber?.number"
      >
        {{ dossier?.customer?.phoneNumber?.number | phoneNumber | async }}
      </p>

      <p
        *ngIf="dossier?.claim?.operationType && dossier?.claim?.glass?.glass"
        class="font-family-semi-bold font-xs uppercase"
      >
        {{ planningCalendarService.getInterventionTypeLabel(dossier, fitter) }}
      </p>
      <div class="action-buttons">
        <sc-button
          id="validateDossier"
          leftIcon="folder"
          (click)="validateDossier.emit(dossier)"
          >{{ 'PlanningBasketAppointment.validateBtn' | translate }}</sc-button
        >
        <sc-button
          *ngIf="!isApmtRequest()"
          id="saveDossier"
          leftIcon="check_bis"
          (click)="saveDossier.emit(dossier)"
          [disabled]="appointmentAlreadyExist(dossier) || !canSave()"
          >{{ 'PlanningBasketAppointment.saveBtn' | translate }}</sc-button
        >
      </div>
    </mat-card-content>
  </mat-card>`,
})
export class PlanningBasketAppointmentComponent implements OnInit {
  @Input() dossier: Dossier;
  @Input() fitter: Fitter;

  @Output()
  validateDossier: EventEmitter<Dossier> = new EventEmitter<Dossier>();

  @Output()
  saveDossier: EventEmitter<Dossier> = new EventEmitter<Dossier>();

  interventionTypeIcons: IconName[];
  partAvailabilityIcon: string;
  originIcon: IconName;
  constructor(
    public planningCalendarService: PlanningCalendarService,
    private readonly planningSandbox: PlanningSandboxService,
    public timezoneService: TimezoneService,
    private readonly configurationService: ConfigurationService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {}

  ngOnInit(): void {
    this.interventionTypeIcons =
      this.planningCalendarService.getEventInterventionIconNames(this.dossier);
    this.partAvailabilityIcon =
      this.planningCalendarService.getPartAvailabilityClass(
        this.getUsedApmt()?.partAvailability
      );
    this.originIcon = this.planningCalendarService.getOriginIcon(
      this.configurationService.getDossierGlobalOriginFromCreationData(
        this.dossier?.creationData,
        this.clientAppId
      )
    );
  }

  isApmtRequest() {
    return !!this.dossier?.requestAppointment && !this.dossier?.appointment;
  }

  displayUnknownReplacementVehicle() {
    return (
      (this.dossier?.appointment?.isReplacementVehicleSelected ??
        this.dossier?.requestAppointment?.isReplacementVehicleSelected) &&
      !this.dossier?.appointment?.assignedReplacementVehicle
    );
  }

  getUsedApmt(): Appointment | RequestAppointment {
    return this.isApmtRequest()
      ? this.dossier?.requestAppointment
      : this.dossier?.appointment;
  }

  appointmentAlreadyExist(dossier: Dossier): boolean {
    return (
      this.planningSandbox.isAppointmentAlreadyExist(dossier) &&
      !this.isApmtRequest()
    );
  }

  canSave() {
    return this.planningSandbox.canValidateDossier(this.dossier);
  }
}
